// LONG-TURN FEEDBACK (authentic Pass-3 repair #4) — status UI only, never another LLM call.
// A coach turn can chain several model calls; after ~9 s the student sees that TWS is still working (and why),
// after ~25 s that their writing is safe. A request that FAILS or TIMES OUT is reported as such — distinctly
// from slow processing — and the student's typed text is preserved by the caller.
export const COACH_WAIT_SLOW_MS = 9000;
export const COACH_WAIT_LONG_MS = 25000;
export const COACH_REQUEST_TIMEOUT_MS = 120000;

export const coachWaitStage = (elapsedMs) =>
  elapsedMs >= COACH_WAIT_LONG_MS ? "long" : elapsedMs >= COACH_WAIT_SLOW_MS ? "slow" : "";

export const coachWaitText = (stage) =>
  stage === "recovering"
    ? "The connection gave up before my reply arrived — I'm still working on it and will bring it here as soon as it's ready. Your writing is safe."
    : stage === "long"
    ? "The Writing Studio is still working with your essay — your writing is safe."
    : stage === "slow"
      ? "The Writing Studio is working with your essay\u2026"
      : "";

// TIMEOUT RECOVERY (AUTHENTIC REPAIR 1) — TIMEOUT ≠ FAILED TURN. Every coach turn carries a client turn_id; when the
// request times out (client timer or gateway 502/504) the SAME turn is recovered by polling a read-only status endpoint —
// never re-sent. A polling window that expires is still not a failure: the student is told the turn is processing and
// given a way to check again. A resend is offered ONLY when the server reports the turn is no longer processing and no
// result was persisted — and it reuses the SAME turn_id, so the server replays instead of running it twice.
export const TURN_RECOVERY_POLL_MS = 3000;
export const TURN_RECOVERY_WINDOW_MS = 120000;
export const newTurnId = () => {
  try { if (globalThis.crypto && globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID(); } catch { /* fall through */ }
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};
export const TURN_RECOVERY_TEXT = {
  processing: "I'm still working on your last message — it hasn't been lost, and nothing needs to be sent again. You can keep working; check for my reply whenever you like.",
  processing_again: "Still working on it. Your message is safe on my end — check again in a moment.",
  not_run: "That message never reached me — nothing was processed and nothing was lost on your side. Send it again when you're ready.",
};
export const TURN_RECOVERY_ACTION = { processing: "Check for my reply", processing_again: "Check again", not_run: "Send it again" };

// FAILURE vs SLOW PROCESSING: a timeout (client timer, or a gateway 502/504) is a slow-processing problem; anything
// else is a technical failure. Neither is ever the student's writing.
export const coachOutcome = (err) => {
  const code = err?.code || "";
  const status = err?.response?.status;
  const msg = String(err?.message || "").toLowerCase();
  if (code === "ECONNABORTED" || code === "ETIMEDOUT" || msg.includes("timeout") || status === 502 || status === 504) return "timeout";
  return "failure";
};

export const coachFailureText = (outcome, repeated = false) => {
  if (outcome === "timeout") {
    return repeated
      ? "That one timed out again — my processing is running slow, not your writing. Everything you've written is saved here. Give it a moment and send again."
      : "That one took too long and timed out on my end — a slow-processing problem, not your writing. Everything you've written is saved. Please send it again.";
  }
  return repeated
    ? "I'm still having trouble on my end — this is a connection issue, not your writing. Your work is all safe here. Give it one more moment and send again."
    : "I wasn't able to process that just now — your work is all still here. Please try sending it again.";
};

// MANUAL-ACTION CLOSURE (repair #1): a typed "done" while TWS is waiting on a map change is the same signal as the
// "I've done this" control (DONE is only the fallback when the change itself could not be observed).
const DONE_RE = /^\s*(ok(ay)?[,.! ]*)?(done|all done|i'?m done|did it|i did it|i did that|i'?ve done (it|this|that)|finished|moved it|changed it|it'?s done|done it)[.! ]*$/i;
export const isDoneSignal = (text) => DONE_RE.test(String(text || ""));
